import { useState } from 'react';

import { Check, ChevronsUpDown, Loader2, Upload } from 'lucide-react';

import { useGetSandboxes } from '@/api/getSandboxes';
import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCanEnterSandbox } from '@/hooks/useCanEnterSandbox';
import { useGame } from '@/hooks/useGame';
import { useSandboxSwitch } from '@/hooks/useGameSandboxSwitch';
import { toaster } from '@/lib/toaster';
import { Permissions } from '@/models/permission';
import { useSandboxContext } from '@/providers';

import { useExportSandbox } from '../api/exportSandbox';

interface SandboxBreadcrumbDropdownProps {
  sandboxCode: string;
}

export const SandboxBreadcrumbDropdown = ({
  sandboxCode,
}: SandboxBreadcrumbDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { selectedSandbox, gameCode } = useSandboxContext();
  const game = useGame();

  const { switchSandbox } = useSandboxSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const { data: sandboxes } = useGetSandboxes({ game_code: game.game_code });

  const currentSandbox = sandboxes?.sandboxes.find(
    (sandbox) => sandbox.code === selectedSandbox,
  );

  const canEnterSandbox = useCanEnterSandbox();

  const exportMutation = useExportSandbox({
    mutationConfig: {
      onSuccess: () => {
        setIsOpen(false);
      },
    },
  });

  const isLoading = exportMutation.isPending;

  const handleSandboxChange = (newSandbox: string) => {
    switchSandbox(gameCode, selectedSandbox, newSandbox);
  };

  const handleExport = () => {
    if (!currentSandbox) {
      toaster.info('No sandbox selected');
      return;
    }

    exportMutation.mutate({ sandboxId: currentSandbox.id });
  };

  return (
    <>
      <span className="truncate max-w-48">
        {currentSandbox?.name ?? sandboxCode}
      </span>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <ChevronsUpDown />
            )}
            <span className="sr-only">Switch sandbox</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-xs p-0">
          <Command>
            <CommandInput placeholder="Search sandbox..." />
            <CommandList className="max-h-48 overflow-y-auto">
              <CommandEmpty>No sandbox found.</CommandEmpty>
              {currentSandbox && (
                <>
                  <CommandGroup heading="Current">
                    <CommandItem
                      value={currentSandbox.name}
                      onSelect={() => {
                        handleSandboxChange(currentSandbox.code);
                      }}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-100" />
                      {currentSandbox.name}
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              <CommandGroup heading="All sandboxes">
                {sandboxes?.sandboxes
                  .filter((sandbox) => sandbox.code !== selectedSandbox)
                  .map((sandbox) => (
                    <CommandItem
                      key={sandbox.code}
                      value={sandbox.code}
                      // Same rule as the Sandbox list: a Sandbox no Grant covers
                      // stays visible but cannot be switched to from here either.
                      disabled={!canEnterSandbox(sandbox.code)}
                      onSelect={() => {
                        handleSandboxChange(sandbox.code);
                      }}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-0" />
                      {sandbox.name}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
            <PermissionCheck oneOf={[Permissions.sandboxes.export]}>
              <CommandSeparator />
              <CommandGroup className="p-1">
                <CommandItem
                  onSelect={handleExport}
                  disabled={isLoading || !currentSandbox}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Export Sandbox
                </CommandItem>
              </CommandGroup>
            </PermissionCheck>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
